import { useState, useRef, useEffect } from 'react'

interface TossModalProps {
  isOpen: boolean
  onClose: () => void
}

export function TossModal({ isOpen, onClose }: TossModalProps) {
  const [isFlipping, setIsFlipping] = useState(false)
  const [rotationDegrees, setRotationDegrees] = useState(0)
  const [result, setResult] = useState<'heads' | 'tails' | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Preload coin audio so it plays instantly with zero latency when user taps
  useEffect(() => {
    try {
      const audio = new Audio('/sound/coin_sound.mp3')
      audio.preload = 'auto'
      audioRef.current = audio
    } catch {
      // Audio fallback
    }
  }, [])

  if (!isOpen) return null

  const handleFlip = () => {
    if (isFlipping) return

    // Immediately play the sound as soon as user taps flip
    if (audioRef.current) {
      try {
        audioRef.current.currentTime = 0
        audioRef.current.play().catch(() => {})
      } catch {
        // Fallback
      }
    } else {
      try {
        const audio = new Audio('/sound/coin_sound.mp3')
        audioRef.current = audio
        audio.play().catch(() => {})
      } catch {
        // Fallback
      }
    }

    setIsFlipping(true)
    setResult(null)

    // Pick random outcome
    const outcome: 'heads' | 'tails' = Math.random() < 0.5 ? 'heads' : 'tails'
    const extraSpins = 360 * 5
    const finalAngle = outcome === 'heads' ? 0 : 180
    const newTotalRotation =
      rotationDegrees + extraSpins + ((finalAngle - (rotationDegrees % 360) + 360) % 360)

    setRotationDegrees(newTotalRotation)

    setTimeout(() => {
      setIsFlipping(false)
      setResult(outcome)
    }, 1300)
  }

  // Realistic Rupee coin textures
  const headsImg = '/images/one_rupee_heads.jpg'
  const tailsImg = '/images/one_rupee_tails.jpg'

  return (
    <div
      className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4 backdrop-blur-xs animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="theme-card rounded-3xl max-w-sm w-full p-6 shadow-2xl space-y-5 text-center relative overflow-hidden border border-[var(--theme-border)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--theme-border)] pb-3">
          <div className="text-left">
            <h3 className="text-xl font-extrabold text-[var(--theme-text)] tracking-tight">🪙 Match Toss</h3>
            <p className="text-xs text-[var(--theme-text-secondary)]">Tap the coin to flip</p>
          </div>
          <button
            onClick={onClose}
            className="size-8 rounded-full hover:bg-black/10 dark:hover:bg-white/10 flex items-center justify-center text-[var(--theme-text-secondary)] hover:text-[var(--theme-text)] font-bold cursor-pointer"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Heads vs Tails Label */}
        <div className="flex justify-around items-center text-xs px-4 py-2.5 bg-black/5 dark:bg-white/5 rounded-xl border border-[var(--theme-border)] font-semibold">
          <div className="flex items-center gap-1.5 text-[var(--theme-text)]">
            <span className="size-2 rounded-full bg-[var(--color-cricket-primary)]" />
            <span>HEADS (Lion Capital)</span>
          </div>
          <span className="opacity-40 font-bold">|</span>
          <div className="flex items-center gap-1.5 text-[var(--theme-text)]">
            <span className="size-2 rounded-full bg-[#E65100]" />
            <span>TAILS (₹1 Side)</span>
          </div>
        </div>

        {/* 3D Realistic Coin Stage */}
        <div className="py-4 flex justify-center items-center perspective-1000">
          <div
            className="size-44 relative cursor-pointer select-none transition-transform"
            style={{
              transformStyle: 'preserve-3d',
              transform: `rotateY(${rotationDegrees}deg)`,
              transition: isFlipping ? 'transform 1.3s cubic-bezier(0.2, 0.85, 0.3, 1)' : 'none',
            }}
            onClick={handleFlip}
          >
            {/* FRONT FACE (HEADS) */}
            <div
              className="absolute inset-0 rounded-full shadow-xl backface-hidden overflow-hidden bg-slate-200 flex items-center justify-center"
              style={{
                boxShadow:
                  '0 12px 28px rgba(0,0,0,0.25), inset 0 2px 4px rgba(255,255,255,0.7), inset 0 -3px 5px rgba(0,0,0,0.3)',
              }}
            >
              <img
                src={headsImg}
                alt="Heads"
                className="w-full h-full object-cover rounded-full pointer-events-none"
              />
            </div>

            {/* BACK FACE (TAILS) */}
            <div
              className="absolute inset-0 rounded-full shadow-xl backface-hidden overflow-hidden bg-slate-200 flex items-center justify-center"
              style={{
                transform: 'rotateY(180deg)',
                boxShadow:
                  '0 12px 28px rgba(0,0,0,0.25), inset 0 2px 4px rgba(255,255,255,0.7), inset 0 -3px 5px rgba(0,0,0,0.3)',
              }}
            >
              <img
                src={tailsImg}
                alt="Tails"
                className="w-full h-full object-cover rounded-full pointer-events-none"
              />
            </div>
          </div>
        </div>

        {/* Toss Result Badge */}
        {result ? (
          <div className="p-3.5 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-2xl border border-[var(--color-cricket-primary)] animate-in zoom-in-95">
            <span className="text-xs font-bold text-[var(--color-cricket-primary)] uppercase tracking-wider block">
              Result
            </span>
            <span className="text-2xl font-black text-[var(--theme-text)] mt-0.5 block">
              {result === 'heads' ? '🪙 HEADS' : '🪙 TAILS'}
            </span>
          </div>
        ) : (
          <div className="text-xs font-medium text-[var(--theme-text-secondary)] py-1">
            Tap coin or button below to flip
          </div>
        )}

        {/* Action Button */}
        <div className="pt-1">
          <button
            type="button"
            onClick={handleFlip}
            disabled={isFlipping}
            className="w-full theme-btn-primary py-3.5 rounded-xl font-bold text-sm shadow-md active:scale-98 transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <span>{isFlipping ? 'Flipping Coin...' : 'Tap to Flip Coin'}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
